'use client'

import type { ComponentProps } from 'react'

import { Input as InputPrimitive } from '@base-ui/react/input'
import { MagnifyingGlassIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

const inputBase = 'w-full min-w-0 rounded-md border border-input-border bg-input-background text-base text-input-foreground transition-colors outline-none placeholder:text-text-placeholder hover:border-input-border-hover focus-visible:border-input-border-focus disabled:cursor-not-allowed disabled:bg-action-disabled disabled:text-text-disabled aria-invalid:border-status-error-foreground'

/*
 * GoodUnited / Input. Figma: 10 / Components / Input.
 * 40px tall, 12px side padding, Roboto / text-base. Border moves to Colors/input/border-focus on focus.
 */
function Input({ className, type = 'text', ...props }: Omit<ComponentProps<'input'>, 'size'> & { className?: string }) {
    return (
        <InputPrimitive
            type={type}
            data-slot='input'
            className={cn(inputBase, 'h-10 px-3 py-2', className)}
            {...props}
        />
    )
}

interface InputGroupSearchProps extends Omit<ComponentProps<'input'>, 'size' | 'type'> {
    containerClassName?: string
}

/* InputGroup/Search: Input with a leading search icon. */
function InputGroupSearch({ className, containerClassName, placeholder = 'Search', ...props }: InputGroupSearchProps) {
    return (
        <div data-slot='input-group' className={cn('relative w-full', containerClassName)}>
            <MagnifyingGlassIcon className='pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-icon-secondary' aria-hidden='true' />
            <InputPrimitive
                type='search'
                data-slot='input'
                placeholder={placeholder}
                aria-label={props['aria-label'] ?? placeholder}
                className={cn(inputBase, 'h-10 py-2 pr-3 pl-10 [&::-webkit-search-cancel-button]:hidden', className)}
                {...props}
            />
        </div>
    )
}

export { Input, InputGroupSearch, inputBase }
